/**
 * MUS-625 - what the tutorial does when the app or tab goes to the background
 * and comes back.
 *
 * Going away always pauses: the pause overlay goes up and the exercise scene is
 * told to pause. Coming back depends on what was on screen:
 *   - an exercise is showing: it STAYS PAUSED under its overlay until the
 *     player taps it or presses Space / Enter. It used to be resumed the
 *     instant the page was visible again, with the player not yet at the keys:
 *     the rest of the phrase went by as misses and it ended in "Try again!".
 *   - only the video is showing: it carries on by itself, as it always has.
 *
 * A pause the player made before leaving is never undone here.
 *
 * The same pair of calls serves the page visibility event and the native app
 * state event. Both can fire for one trip to the background, in either order,
 * so each call is safe to repeat.
 *
 * The side effects are injected so this can be unit tested, against the real
 * exercise scenes, without rendering the tutorial template.
 */

export interface TutorialBackgroundPauseDeps {
  /** True while the pause overlay is already up. */
  isPauseOverlayShown: () => boolean;
  /** True while an exercise is on screen, over the video. */
  isExerciseShowing: () => boolean;
  showPauseOverlay: () => void;
  hidePauseOverlay: () => void;
  pauseExercise: () => void;
  resumeExercise: () => void;
}

export interface TutorialBackgroundPause {
  /** The page was hidden, or the app left the foreground. */
  onBackground: () => void;
  /** The page is visible again, or the app is back in the foreground. */
  onForeground: () => void;
}

export function makeTutorialBackgroundPause(
  deps: TutorialBackgroundPauseDeps,
): TutorialBackgroundPause {
  // True while the overlay is up because of the background, not the player.
  let pausedByBackground = false;

  return {
    onBackground: () => {
      if (!deps.isPauseOverlayShown()) pausedByBackground = true;
      deps.showPauseOverlay();
      deps.pauseExercise();
    },
    onForeground: () => {
      if (!pausedByBackground) return;
      pausedByBackground = false;
      if (deps.isExerciseShowing()) return;
      deps.hidePauseOverlay();
      deps.resumeExercise();
    },
  };
}

/**
 * MUS-626 - closing the "connect your keyboard" prompt takes the pause overlay
 * down with it. That is only right when nothing is held paused underneath: an
 * exercise the scene still holds paused (the app went to the background while
 * it ran) keeps its overlay, so the player's tap resumes it. Without this the
 * exercise is left frozen with nothing on screen to say why.
 */
export function keepsPauseOverlayAfterMidiPrompt(state: {
  exerciseShowing: boolean;
  exercisePaused: boolean;
}): boolean {
  return state.exerciseShowing && state.exercisePaused;
}
